import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { clsx } from 'clsx';
import {
  dismissNotification,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification,
} from '@/api/notifications';

function navigateForNotification(navigate: ReturnType<typeof useNavigate>, n: AppNotification) {
  const route = n.action.route;
  const q = n.action.query ?? {};
  const search = new URLSearchParams(q).toString();
  const path = search ? `${route}?${search}` : route;
  navigate(path);
}

export function NotificationBell() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { notifications, unreadCount: count } = await fetchNotifications({ limit: 12 });
      setItems(notifications);
      setUnreadCount(count);
    } catch {
      /* ignore poll errors */
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePanelPosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setPanelPos({
      top: rect.top,
      left: rect.right + 8,
    });
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    void refresh();
    updatePanelPosition();

    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onReposition = () => updatePanelPosition();

    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, refresh, updatePanelPosition]);

  const handleClick = async (n: AppNotification) => {
    if (!n.read) {
      await markNotificationRead(n.id);
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    navigateForNotification(navigate, n);
  };

  const panel =
    open &&
    createPortal(
      <div
        ref={panelRef}
        className="dropdown-menu-panel fixed z-[300] flex max-h-[min(24rem,70vh)] w-80 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-popover"
        style={{ top: panelPos.top, left: panelPos.left }}
        role="dialog"
        aria-label={t('notifications.title')}
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground">{t('notifications.title')}</span>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline"
                onClick={() => void markAllNotificationsRead().then(refresh)}
              >
                {t('notifications.markAllRead')}
              </button>
            )}
            <button
              type="button"
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
            >
              {t('notifications.viewAll')}
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && items.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">{t('common.loading')}</p>
          ) : items.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">{t('notifications.empty')}</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => void handleClick(n)}
                className={clsx(
                  'w-full border-b border-border-muted px-3 py-2.5 text-left transition-colors hover:bg-muted/40',
                  !n.read && 'bg-primary/5',
                )}
              >
                <p className="truncate text-sm font-semibold text-foreground">{n.title}</p>
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
              </button>
            ))
          )}
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            if (next) updatePanelPosition();
            return next;
          });
        }}
        className={clsx(
          'relative flex h-12 w-12 items-center justify-center rounded-xl transition-all',
          open
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground/60 hover:bg-muted/50 hover:text-foreground',
        )}
        aria-label={t('notifications.bellAria')}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell size={20} strokeWidth={2} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {panel}
    </>
  );
}

export function NotificationsList({
  onNavigate,
}: {
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const { notifications } = await fetchNotifications({ limit: 50 });
        setItems(notifications);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleClick = async (n: AppNotification) => {
    if (!n.read) await markNotificationRead(n.id);
    onNavigate?.();
    navigateForNotification(navigate, n);
  };

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  if (items.length === 0) {
    return <p className="p-6 text-sm text-muted-foreground">{t('notifications.empty')}</p>;
  }

  return (
    <ul className="divide-y divide-border">
      {items.map((n) => (
        <li key={n.id}>
          <button
            type="button"
            onClick={() => void handleClick(n)}
            className={clsx(
              'flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/30',
              !n.read && 'bg-primary/5',
            )}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground">{n.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{n.body}</p>
            </div>
            <button
              type="button"
              className="shrink-0 text-[10px] text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                void dismissNotification(n.id).then(() =>
                  setItems((prev) => prev.filter((x) => x.id !== n.id)),
                );
              }}
            >
              {t('notifications.dismiss')}
            </button>
          </button>
        </li>
      ))}
    </ul>
  );
}
